import type { EpocusBillingStatus } from "$global-providers/rxdb-provider/collections/epocus-billing-status";
import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import { examTypeLabel } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { useCallback, useMemo } from "react";
import type { RxCollection } from "rxdb";
import { useRxData } from "rxdb-hooks";
import { incompleteBillingReasonLabels } from "../../_lib/billing/billing-status-labels";
import { useMyIncompleteBillingStatuses } from "../../_lib/billing/use-my-incomplete-billing-statuses";

export const OVERDUE_AFTER_MS = 72 * 60 * 60 * 1000;

export type IncompleteStudy = {
  examId: string;
  encounterId: string;
  worksheet: string;
  date: string;
  reasons: string[];
  overdue: boolean;
};

type EncounterFields = Pick<EpocusEncounter, "id" | "date">;

export const toIncompleteStudies = ({
  statuses,
  encounters,
  now,
}: {
  statuses: readonly EpocusBillingStatus[];
  encounters: readonly EncounterFields[];
  now: Date;
}): IncompleteStudy[] => {
  const encounterById = new Map(
    encounters.map((encounter) => [encounter.id, encounter]),
  );

  return statuses
    .map((status) => {
      const incompleteSince =
        status.examSignedAt ?? status.examCreatedAt ?? status.meta.createdAt;
      return {
        incompleteSince,
        study: {
          examId: status.examId,
          encounterId: status.encounterId,
          worksheet: status.examType ? examTypeLabel(status.examType) : "Study",
          date: encounterById.get(status.encounterId)?.date ?? incompleteSince,
          reasons: incompleteBillingReasonLabels(status),
          overdue:
            now.getTime() - new Date(incompleteSince).getTime() >
            OVERDUE_AFTER_MS,
        },
      };
    })
    .sort((a, b) => a.incompleteSince.localeCompare(b.incompleteSince))
    .map(({ study }) => study);
};

export const useIncompleteStudies = (): IncompleteStudy[] => {
  const statuses = useMyIncompleteBillingStatuses();
  const { getCurrentTime } = useServerTime();

  const encounterIds = useMemo(
    () => [...new Set(statuses.map(({ encounterId }) => encounterId))],
    [statuses],
  );

  const encounterQuery = useCallback(
    (collection: RxCollection<EpocusEncounter>) =>
      encounterIds.length > 0
        ? collection.find({ selector: { id: { $in: encounterIds } } })
        : undefined,
    [encounterIds],
  );

  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    encounterQuery,
  );

  return useMemo(
    () =>
      statuses.length === 0
        ? []
        : toIncompleteStudies({
            statuses,
            encounters: encounters ?? [],
            now: getCurrentTime(),
          }),
    [statuses, encounters, getCurrentTime],
  );
};
